import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Search, Filter, Plus, Mail, Clock, User, 
  AlertTriangle, CheckCircle, XCircle, MessageSquare,
  FileText, Send, Eye, Users, BarChart3, RefreshCw
} from 'lucide-react';
import { supportService } from '../services/supportService';
import { getAdminUser } from '../utils/auth';

const SupportPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get current admin info
  const currentAdmin = getAdminUser();
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [showArchived, setShowArchived] = useState(false);
  
  // Selected ticket for detail view
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Reply/Note states
  const [replyMessage, setReplyMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deletingTicket, setDeletingTicket] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState({
    total_tickets: 0,
    unassigned_tickets: 0,
    archived_tickets: 0,
    status_counts: {}
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [statusFilter, sortBy, showArchived]);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm]);

  const fetchAllData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Fetch tickets and statistics with current filters
      const filters = { 
        sort_by: sortBy,
        show_archived: showArchived
      };
      
      // Add status filter only if not 'all' and not showing archived
      if (statusFilter !== 'all' && !showArchived) {
        filters.status = statusFilter;
      }
      
      const [ticketsData, statsData] = await Promise.all([
        supportService.getAllTickets(filters),
        supportService.getSupportStats().catch(() => ({
          total_tickets: 0,
          unassigned_tickets: 0,
          archived_tickets: 0,
          status_counts: {}
        }))
      ]);
      
      setTickets(ticketsData);
      setStats(statsData);
      
      // Show success toast for manual refresh only
      if (isRefresh) {
        toast.success('Data refreshed successfully');
      }
    } catch (err) {
      console.error('Error fetching support data:', err);
      if (!isRefresh) {
        setError(err.response?.data?.detail || 'Failed to load support tickets');
      } else {
        toast.error('Failed to refresh data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAllData(true);
  };

  const filterTickets = () => {
    let filtered = tickets;

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.ticket_number.toLowerCase().includes(search) ||
        ticket.customer_name.toLowerCase().includes(search) ||
        ticket.customer_email.toLowerCase().includes(search) ||
        ticket.subject.toLowerCase().includes(search)
      );
    }

    setFilteredTickets(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-blue-600 bg-blue-50';
      case 'in_progress': return 'text-orange-600 bg-orange-50';
      case 'resolved': return 'text-green-600 bg-green-50';
      case 'closed': return 'text-gray-600 bg-gray-50';
      case 'archived': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
    setLoadingMessages(true);
    
    try {
      const messages = await supportService.getTicketMessages(ticket.id);
      setTicketMessages(messages);
    } catch (err) {
      console.error('Error loading ticket messages:', err);
      toast.error('Failed to load ticket messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleArchiveTicket = async (ticketId, ticketNumber) => {
    if (!window.confirm(`Are you sure you want to archive ticket ${ticketNumber}?`)) {
      return;
    }

    try {
      await supportService.archiveTicket(ticketId);
      toast.success('Ticket archived successfully');
      fetchAllData();
      if (showTicketDetail && selectedTicket?.id === ticketId) {
        setShowTicketDetail(false);
      }
    } catch (err) {
      console.error('Error archiving ticket:', err);
      toast.error('Failed to archive ticket');
    }
  };

  const handleReactivateTicket = async (ticketId, ticketNumber) => {
    if (!window.confirm(`Are you sure you want to reactivate ticket ${ticketNumber}?`)) {
      return;
    }

    try {
      await supportService.reactivateTicket(ticketId);
      toast.success('Ticket reactivated successfully');
      fetchAllData();
      if (showTicketDetail && selectedTicket?.id === ticketId) {
        setShowTicketDetail(false);
      }
    } catch (err) {
      console.error('Error reactivating ticket:', err);
      toast.error('Failed to reactivate ticket');
    }
  };

  const handleDeleteTicket = (ticketId, ticketNumber) => {
    setTicketToDelete({ id: ticketId, number: ticketNumber });
    setShowDeleteConfirm(true);
    setDeletePassword('');
  };

  const handleConfirmDelete = async () => {
    if (!deletePassword.trim() || !ticketToDelete) return;
    
    setDeletingTicket(true);
    try {
      await supportService.deleteTicket(ticketToDelete.id, deletePassword);
      toast.success('Ticket deleted successfully');
      fetchAllData();
      setShowDeleteConfirm(false);
      if (showTicketDetail && selectedTicket?.id === ticketToDelete.id) {
        setShowTicketDetail(false);
      }
    } catch (err) {
      console.error('Error deleting ticket:', err);
      if (err.response?.status === 403) {
        toast.error('Incorrect admin password');
      } else {
        toast.error('Failed to delete ticket');
      }
    } finally {
      setDeletingTicket(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      await supportService.addTicketMessage(selectedTicket.id, {
        message: replyMessage,
        is_internal: false
      });
      
      setReplyMessage('');
      toast.success('Reply sent successfully');
      
      // Refresh messages and ticket data to show status updates
      const [messages, updatedTicket] = await Promise.all([
        supportService.getTicketMessages(selectedTicket.id),
        supportService.getTicketById(selectedTicket.id).catch(() => selectedTicket)
      ]);
      
      setTicketMessages(messages);
      setSelectedTicket(updatedTicket);
      
      // Refresh the main tickets list to show updated status
      fetchAllData();
    } catch (err) {
      console.error('Error sending reply:', err);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleAddInternalNote = async () => {
    if (!internalNote.trim() || !selectedTicket) return;
    
    try {
      await supportService.addInternalNote(selectedTicket.id, internalNote);
      setInternalNote('');
      toast.success('Internal note added');
      
      // Refresh ticket data
      fetchAllData();
    } catch (err) {
      console.error('Error adding internal note:', err);
      toast.error('Failed to add internal note');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await supportService.updateTicket(ticketId, { status: newStatus });
      toast.success('Ticket status updated');
      fetchAllData();
      
      // Update the selected ticket if it's the one being changed
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating ticket status:', err);
      toast.error('Failed to update ticket status');
    }
  };

  const handleClearTestData = async () => {
    if (!window.confirm('Are you sure you want to clear all test support tickets? This will remove all example/test tickets from the database.')) {
      return;
    }

    try {
      const result = await supportService.clearTestData();
      toast.success(result.message);
      fetchAllData();
    } catch (err) {
      console.error('Error clearing test data:', err);
      toast.error('Failed to clear test data');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Support Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage customer support tickets and conversations</p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setShowArchived(!showArchived);
                // This will trigger fetchAllData via useEffect
              }}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 relative"
            >
              <BarChart3 size={16} />
              {showArchived ? 'Show Active' : 'All Archives'}
              {!showArchived && stats.archived_tickets > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-purple-600 rounded-full">
                  {stats.archived_tickets}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Tickets</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_tickets}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Resolved</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.status_counts.resolved || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Archived</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.archived_tickets || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search tickets by number, customer name, email, or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                fetchAllData();
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                fetchAllData();
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="latest">Latest to Oldest</option>
              <option value="oldest">Oldest to Latest</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Tickets List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading support tickets...</p>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Support Tickets ({filteredTickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredTickets.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No support tickets found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Ticket #</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Customer</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Subject</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Created</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map((ticket) => (
                        <tr key={ticket.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <span className="font-mono text-sm text-blue-600">
                              {ticket.ticket_number}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{ticket.customer_name}</p>
                              <p className="text-sm text-gray-500">{ticket.customer_email}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-gray-900 line-clamp-2">{ticket.subject}</p>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-500">
                            {formatDate(ticket.created_at)}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTicketClick(ticket)}
                                className="flex items-center gap-1"
                              >
                                <Eye size={14} />
                                View
                              </Button>
                              {ticket.archived ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReactivateTicket(ticket.id, ticket.ticket_number)}
                                  className="flex items-center gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <CheckCircle size={14} />
                                  Active
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleArchiveTicket(ticket.id, ticket.ticket_number)}
                                  className="flex items-center gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                >
                                  <BarChart3 size={14} />
                                  Archive
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTicket(ticket.id, ticket.ticket_number)}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle size={14} />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Ticket Detail Dialog */}
      <Dialog open={showTicketDetail} onOpenChange={setShowTicketDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Ticket #{selectedTicket?.ticket_number}
            </DialogTitle>
            <DialogDescription>
              Support ticket conversation and details
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-6">
              {/* Ticket Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Name:</span> {selectedTicket.customer_name}</p>
                    <p><span className="font-medium">Email:</span> {selectedTicket.customer_email}</p>
                    <p><span className="font-medium">Phone:</span> {selectedTicket.customer_phone}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Ticket Details</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Subject:</span> {selectedTicket.subject}</p>
                    <p><span className="font-medium">Status:</span>
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                        className="ml-2 px-2 py-1 border rounded text-xs"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </p>
                  </div>
                </div>
              </div>

              {/* Original Message */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Original Message</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {formatDate(selectedTicket.created_at)}
                  </p>
                </div>
              </div>

              {/* Internal Notes */}
              {selectedTicket.internal_notes && selectedTicket.internal_notes.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Internal Notes</h4>
                  <div className="space-y-2">
                    {selectedTicket.internal_notes.map((note, index) => (
                      <div key={index} className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                        <p className="text-gray-700">{note.note}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {note.author} - {formatDate(note.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conversation Messages */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Conversation</h4>
                {loadingMessages ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-60 overflow-y-auto">
                    {ticketMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-4 rounded-lg ${
                          message.sender_type === 'admin'
                            ? 'bg-blue-50 border-l-4 border-blue-400 ml-8'
                            : 'bg-green-50 border-l-4 border-green-400 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-gray-900">
                            {message.sender_name} {message.is_internal && '(Internal)'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Section */}
              <div className="border-t pt-4">
                <div className="space-y-4">
                  {/* Customer Reply */}
                  <div>
                    <Label htmlFor="reply">Reply to Customer</Label>
                    <textarea
                      id="reply"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply to the customer..."
                      className="w-full mt-1 p-3 border border-gray-300 rounded-md resize-none"
                      rows={4}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        onClick={handleSendReply}
                        disabled={!replyMessage.trim() || sendingReply}
                      >
                        {sendingReply ? (
                          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Send size={14} className="mr-2" />
                        )}
                        Send Reply
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {/* Internal Note */}
                  <div>
                    <Label htmlFor="internal-note">Add Internal Note</Label>
                    <textarea
                      id="internal-note"
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="Add internal note (only visible to admin team)..."
                      className="w-full mt-1 p-3 border border-gray-300 rounded-md resize-none"
                      rows={3}
                    />
                    <Button
                      variant="outline"
                      onClick={handleAddInternalNote}
                      disabled={!internalNote.trim()}
                      className="mt-2"
                    >
                      <FileText size={14} className="mr-2" />
                      Add Internal Note
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <Button 
                variant="outline" 
                onClick={() => selectedTicket && handleDeleteTicket(selectedTicket.id, selectedTicket.ticket_number)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <XCircle size={14} className="mr-2" />
                Delete Ticket
              </Button>
              <Button variant="outline" onClick={() => setShowTicketDetail(false)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Delete Ticket
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete ticket #{ticketToDelete?.number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-password">Enter your Password</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={!deletePassword.trim() || deletingTicket}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingTicket ? (
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <XCircle size={14} className="mr-2" />
              )}
              Delete Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;